import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

const SURVEY_CHECK_ITEMS = [
  "Toegang tot locatie gecontroleerd",
  "Werktekening aanwezig",
  "Installatiegegevens kloppen",
  "Risico's besproken",
  "Foto's van beginsituatie gemaakt",
];

const STORAGE_KEY = "logboek24.monteur.siteSurveyChecklist";

export default function MonteurSiteSurveyPage() {
  const navigate = useNavigate();
  const [werkbonnen, setWerkbonnen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checklist, setChecklist] = useState(() => loadChecklist());

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");
        const res = await api.get("/werkbonnen/mine");
        setWerkbonnen(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError(err?.response?.data?.message || "Site survey werkbonnen laden mislukt");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(checklist));
    } catch {
      // ignore local storage issues
    }
  }, [checklist]);

  const geplandeWerkbonnen = useMemo(() => {
    const today = new Date();
    return (werkbonnen || [])
      .filter((item) => item?.plannedDate)
      .sort((a, b) => new Date(a.plannedDate) - new Date(b.plannedDate))
      .filter((item) => new Date(item.plannedDate) >= startOfDay(today))
      .slice(0, 8);
  }, [werkbonnen]);

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Site Survey</h2>
        <p className="mt-1 text-sm text-gray-600">
          Gebruik deze pagina voor voorbereiding op locatie en snelle doorstart naar opnames.
        </p>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Checklist vandaag</h3>
          <button
            type="button"
            onClick={() => setChecklist({})}
            className="rounded border px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>

        <div className="space-y-2">
          {SURVEY_CHECK_ITEMS.map((item) => (
            <label key={item} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(checklist[item])}
                onChange={(event) =>
                  setChecklist((prev) => ({
                    ...prev,
                    [item]: event.target.checked,
                  }))
                }
              />
              <span>{item}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Geplande werkbonnen</h3>
          <button
            type="button"
            onClick={() => navigate("/monteur/opnames")}
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
          >
            Naar opnames
          </button>
        </div>

        {loading && <p className="text-sm text-gray-500">Laden...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && !error && geplandeWerkbonnen.length === 0 && (
          <p className="text-sm text-gray-500">Geen geplande werkbonnen gevonden.</p>
        )}

        <div className="space-y-2">
          {geplandeWerkbonnen.map((item) => (
            <div key={item._id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {item.werkbonNummer || item.nummer || "Werkbon"}
                </p>
                <p className="text-xs text-gray-600">
                  {formatDate(item.plannedDate)} | {item.locationText || item.opdracht?.locatie || "Locatie onbekend"}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/monteur/werkbonnen/${item._id}`)}
                  className="rounded border px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                >
                  Open werkbon
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/monteur/opnames")}
                  className="rounded bg-slate-800 px-2.5 py-1.5 text-xs text-white hover:bg-slate-900"
                >
                  Opname
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function loadChecklist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}
